import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import useUserStore from '../stores/userStore';
import useInventoryStore from '../stores/inventoryStore';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';
import WebApp from '@twa-dev/sdk';
import { FaSteam } from 'react-icons/fa';
import { FaInfoCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 16px;
  padding-top: 50px;
`;

const BackButton = styled.button`
  position: absolute;
  top: 24px;
  left: 16px;
  background: rgba(255,255,255,0.08);
  border: none;
  border-radius: 12px;
  color: ${props => props.theme.text.primary};
  font-size: 16px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const BigIcon = styled.div`
  font-size: 120px;
  margin: 48px 0 16px 0;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.text.primary};
  text-align: center;
  margin: 0 0 16px 0;
`;

const Description = styled.p`
  color: ${props => props.theme.text.secondary};
  text-align: center;
  font-size: 16px;
  margin-bottom: 18px;
`;

const TradeUrlInput = styled.textarea`
  width: 100%;
  min-height: 60px;
  background: rgba(255,255,255,0.07);
  border: 1px solid ${props => props.hasError ? '#ff4444' : 'rgba(255,255,255,0.12)'};
  border-radius: 12px;
  padding: 12px 16px;
  color: ${props => props.theme.text.primary};
  font-size: 15px;
  margin-bottom: 24px;
  resize: none;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff4444' : props.theme.primary};
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  font-size: 12px;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: rgba(255, 68, 68, 0.1);
  border-radius: 8px;
  border-left: 3px solid #ff4444;
  text-align: left;
`;

const HelpBox = styled.div`
  background: transparent;
  border-radius: 18px;
  padding: 28px 24px 20px 24px;
  margin: 0 0 12px 0;
  color: #fff;
  font-size: 15px;
  max-width: 420px;
  width: 100%;
  position: relative;
`;

const HelpTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 16px;
  color: #fff;

  svg {
    font-size: 32px !important;
  }
`;

const HelpStep = styled.div`
  margin-bottom: 7px;
  line-height: 1.6;
`;

const HelpLink = styled.a`
  color: #4b69ff;
  word-break: break-all;
  text-decoration: underline;
  font-family: monospace;
  font-size: 14px;
`;

const CloseHelpButton = styled.button`
  background: ${({ theme }) => theme.primary};
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 12px 28px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(34,158,217,0.10);
  transition: background 0.2s;
  &:hover { background: #005ecb; }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px;
  font-size: 18px;
  font-weight: 600;
  margin-top: 8px;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Компонент помощи для ошибки trade link
const TradeErrorHelp = () => {
  const { t } = useTranslation();
  return (
    <HelpBox>
      <HelpTitle>
        <FaInfoCircle style={{ color: '#4b69ff', fontSize: 22 }} />
        {t('tradeErrorHelp.title')}
      </HelpTitle>
      <HelpStep>{t('tradeErrorHelp.step1')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step1_1')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step1_2')}</HelpStep>
      <HelpStep style={{ marginTop: 10 }}>{t('tradeErrorHelp.step2')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step2_1')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step2_2')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step2_3')}</HelpStep>
      <HelpStep style={{ marginTop: 10 }}>{t('tradeErrorHelp.step3')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step3_1')}</HelpStep>
      <HelpLink href="https://steamcommunity.com/tradeoffer/new/?partner=XXXXXXX&token=XXXX" target="_blank" rel="noopener noreferrer">
        https://steamcommunity.com/tradeoffer/new/?partner=XXXXXXX&token=XXXX
      </HelpLink>
      <HelpStep style={{ marginTop: 10 }}>{t('tradeErrorHelp.step4')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step4_1')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step4_2')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step4_3')}</HelpStep>
    </HelpBox>
  );
};

const WithdrawPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useUserStore(state => state.user);
  const weapon = location.state?.weapon;
  console.log('User context in WithdrawPage:', user);
  const [tradeUrl, setTradeUrl] = useState(user?.tradeLink || user?.trade_link || user?.trade_url || '');
  const [isLoading, setIsLoading] = useState(false);
  const updateItemStatus = useInventoryStore(state => state.updateItemStatus);
  const [showTradeHelp, setShowTradeHelp] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    WebApp.BackButton.show();
    WebApp.BackButton.onClick(() => navigate(-1));
    return () => {
      WebApp.BackButton.hide();
      WebApp.BackButton.offClick();
    };
  }, [navigate]);

  useEffect(() => {
    if (user?.tradeLink) {
      setTradeUrl(user.tradeLink);
    }
  }, [user?.tradeLink]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(''); // Очищаем ошибку при новом запросе

    try {
      await apiService.requestWithdrawal(weapon.id, tradeUrl);
      updateItemStatus(weapon.id, 'pending');
      toast.success(t('withdraw.success'), { position: 'top-center', autoClose: 2000 });
      navigate(-1);
    } catch (error) {
      console.error('Error requesting withdrawal:', error);

      // Проверяем, есть ли сообщение об ошибке в ответе
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError(t('withdraw.errorInstruction'));
      }

      toast.error(t('withdraw.error'), { position: 'top-center', autoClose: 3000 });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Container>
      <BigIcon><FaSteam style={{ fontSize: 120, color: '#4b69ff' }} /></BigIcon>
      <Title>{t('withdraw.title', { item: weapon?.marketHashName || weapon?.name || 'предмет' })}</Title>
      <Description dangerouslySetInnerHTML={{ __html: t('withdraw.description') }} />
      <form style={{ width: '100%' }} onSubmit={handleSubmit}>
        <TradeUrlInput
          value={tradeUrl}
          onChange={e => {
            setTradeUrl(e.target.value);
            // Очищаем ошибку при изменении URL
            if (error) {
              setError('');
            }
          }}
          placeholder={t('withdraw.inputPlaceholder')}
          hasError={!!error}
        />
        {error && (
          <ErrorMessage>
            {error} <a href="#" style={{ color: '#4b69ff', textDecoration: 'underline' }} onClick={e => { e.preventDefault(); setShowTradeHelp(true); }}>{t('withdraw.instruction')}</a>
          </ErrorMessage>
        )}
        <SubmitButton type="submit" disabled={isLoading || !tradeUrl}>
          {isLoading ? t('withdraw.sending') : t('withdraw.submit')}
        </SubmitButton>
      </form>
      {showTradeHelp && (
        <div style={{
          position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: 'rgb(35, 35, 58)', borderRadius: 20, padding: 0, maxWidth: 440, width: '92vw', color: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'hidden', paddingBottom: 32
          }}>
            <TradeErrorHelp />
            <CloseHelpButton onClick={() => setShowTradeHelp(false)}>
              {t('withdraw.close')}
            </CloseHelpButton>
          </div>
        </div>
      )}
    </Container>
  );
};

export default WithdrawPage; 